import * as React from 'react';

import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { useCartServices } from './hooks/useCartServices';
import { useCurrentVariant } from './hooks/useCurrentVariant';

interface ProductDetailContextValue extends ReturnType<typeof useCartServices> {
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  currentVariant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
}

interface Props {
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
}

const Context = React.createContext<ProductDetailContextValue>(null!);

export const useProductDetailContext = (throwError = true) => {
  const value = React.useContext(Context);
  if (!value && throwError) {
    throw Error(
      'useProductDetailContext must be within ProductDetailContextProvider.'
    );
  }

  return value;
};

export const ProductDetailContextProvider = ({
  children,
  product,
}: React.PropsWithChildren<Props>) => {
  const currentVariant = useCurrentVariant(product.variants);
  const cartServices = useCartServices(product, currentVariant);

  const value: ProductDetailContextValue = React.useMemo(
    () => ({
      product,
      currentVariant,
      ...cartServices,
    }),
    [product, currentVariant, cartServices]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};
